/**
 * Fixed-step accumulator: the sim advances in exact TICK_MS steps no matter the frame
 * rate; the renderer interpolates with `alpha` (fraction of the next step elapsed).
 *
 * Input-triggered early steps (`stepEarly`) run the next step before its whole stepMs has
 * elapsed and carry the difference as debt (the accumulator goes negative), so the sim never
 * runs faster than real time on average and leads it by at most `stepMs - minMs`.
 */
export interface FixedStepOptions {
  stepMs: number;
  /** Frame deltas above this are clamped (tab was hidden, debugger pause...). */
  maxFrameMs?: number;
  /** Hard cap on steps per advance() so a slow sim can't spiral. Leftover time is dropped. */
  maxStepsPerFrame?: number;
}

export class FixedStepAccumulator {
  readonly stepMs: number;
  private readonly maxFrameMs: number;
  private readonly maxSteps: number;
  private acc = 0;
  /** Simulation speed multiplier (spectator fast-forward). 0 pauses. */
  timeScale = 1;

  constructor(opts: FixedStepOptions) {
    if (!(opts.stepMs > 0)) throw new Error('stepMs must be > 0');
    this.stepMs = opts.stepMs;
    this.maxFrameMs = opts.maxFrameMs ?? 250;
    this.maxSteps = opts.maxStepsPerFrame ?? 20;
  }

  /** Add a frame's real elapsed time and run `step` once per whole step. Returns steps run. */
  advance(frameMs: number, step: () => void): number {
    const dt = Number.isFinite(frameMs) ? Math.min(Math.max(frameMs, 0), this.maxFrameMs) : 0;
    this.acc += dt * this.timeScale;
    let n = 0;
    while (this.acc >= this.stepMs && n < this.maxSteps) {
      step();
      this.acc -= this.stepMs;
      n++;
    }
    // Spiral-of-death guard: never carry more than one step of backlog.
    if (this.acc >= this.stepMs) this.acc = this.acc % this.stepMs;
    return n;
  }

  /**
   * Run the next step now if at least `minMs` of it has elapsed (input-triggered early tick).
   * Returns false (and does nothing) if it is too soon or the clock is paused.
   */
  stepEarly(minMs: number, step: () => void): boolean {
    if (!(this.timeScale > 0) || this.acc < minMs) return false;
    step();
    this.acc -= this.stepMs;
    return true;
  }

  /** Real ms until `stepEarly(minMs)` would be allowed (Infinity while paused). */
  msUntilEarly(minMs: number): number {
    if (!(this.timeScale > 0)) return Infinity;
    return Math.max(0, minMs - this.acc) / this.timeScale;
  }

  /**
   * Sim ms elapsed since the latest step (the sim's "present" is latest step time + this).
   * Negative right after an early step: the sim is ahead of real time by that much.
   */
  get sinceStepMs(): number {
    return this.acc;
  }

  /** Interpolation factor in [0, 1) (0 while the sim is ahead after an early step). */
  get alpha(): number {
    return Math.min(1, Math.max(0, this.acc / this.stepMs));
  }

  reset(): void {
    this.acc = 0;
  }
}
